// PLANUL 2D PE PARDOSEALA MACHETEI 3D: ce arată planul de nivel — numele și
// aria fiecărei camere, conturul mobilierului, numele pieselor sanitare —
// desenat pe fața pardoselii, ca țevile să se citească față de WC, lavoar
// sau bucătărie, nu față de o podea goală. Un grup în cadrul documentului
// (+y sud), pus în `grup`-ul stratului de instalații, deci în `lume`.
//
// Etichetele sunt `Sprite`-uri cu text desenat pe un canvas: stau mereu cu
// fața la cameră, deci se citesc din orice unghi, și nu cer un font 3D.
// Fără canvas 2D (jsdom, în teste) eticheta rămâne o pastilă albă.

import * as THREE from "three";
import type { Cheie } from "../i18n";
import { ro } from "../i18n/ro";
import type { StoreyPlan } from "../viewer/canonical";
import { PIESE_SANITARE } from "./model";

type Piesa = NonNullable<StoreyPlan["mobilier"]>[number];

/** Cât stă desenul deasupra feței pardoselii (care e la 0,006), ca să nu
 *  pâlpâie în ea. */
const PESTE_PARDOSEALA_M = 0.012;
/** Înălțimea etichetei de cameră, respectiv de piesă, în metri. */
const ETICHETA_CAMERA_M = 0.34;
const ETICHETA_PIESA_M = 0.22;
/** Camerele mai mici de atât (un șliț, o nișă) nu primesc etichetă. */
const ARIE_MIN_M2 = 1.0;
const LINIE = 0x8a7660;   // conturul mobilierului, ca în plan (`arhMobilier`)

export interface Nume {
  camera(kind: string): string;
  piesa(kind: string): string;
}

export interface PlanPeJos {
  grup: THREE.Group;
  elibereaza(): void;
}

/** Numele din catalog ale camerelor (`kind.*`) și ale pieselor (`mobilier.*`);
 *  un fel fără text în catalog rămâne cu codul lui, nu cu cheia. */
export function numeDinCatalog(t: (cheie: Cheie) => string): Nume {
  const din = (cheie: string, altfel: string) => (cheie in ro ? t(cheie as Cheie) : altfel);
  return {
    camera: (k) => din(`kind.${k}`, k),
    piesa: (k) => din(`mobilier.${k}`, k),
  };
}

export function construiestePlanPeJos(plansee: readonly StoreyPlan[], nume: Nume, zBaza: number): PlanPeJos {
  const grup = new THREE.Group();
  grup.name = "instalatii-plan";
  const deArs: { dispose(): void }[] = [];

  const contururi: number[] = [];
  for (const plan of plansee) {
    const z = (plan.cota ?? 0) - zBaza + PESTE_PARDOSEALA_M;
    for (const p of plan.mobilier ?? []) {
      const c = colturi(p);
      for (let i = 0; i < 4; i++) {
        const a = c[i], b = c[(i + 1) % 4];
        contururi.push(a[0], a[1], z, b[0], b[1], z);
      }
      if (p.kind in PIESE_SANITARE) {
        grup.add(eticheta(nume.piesa(p.kind), ETICHETA_PIESA_M,
                          new THREE.Vector3(p.cx, p.cy, z + PIESE_SANITARE[p.kind] + 0.08), deArs));
      }
    }
    for (const c of plan.spaces) {
      const arie = c.area ?? 0;
      if (!c.kind || !(arie > ARIE_MIN_M2) || c.poly.length < 3) continue;
      const [cx, cy] = centru(c.poly);
      grup.add(eticheta(`${nume.camera(c.kind)} · ${arie.toFixed(2)} m²`, ETICHETA_CAMERA_M,
                        new THREE.Vector3(cx, cy, z + 0.05), deArs));
    }
  }
  if (contururi.length) {
    const geo = new THREE.BufferGeometry();
    geo.setAttribute("position", new THREE.Float32BufferAttribute(contururi, 3));
    const mat = new THREE.LineBasicMaterial({ color: LINIE });
    deArs.push(geo, mat);
    const linii = new THREE.LineSegments(geo, mat);
    linii.name = "mobilier";
    linii.renderOrder = 3;   // peste pardoseala translucidă
    grup.add(linii);
  }
  return {
    grup,
    elibereaza: () => {
      grup.removeFromParent();
      deArs.forEach((x) => x.dispose());
    },
  };
}

/** Colțurile unei piese: dreptunghiul ei rotit cu `deg` în jurul centrului,
 *  cum îl pune planul (`rotate(deg)`) și cutia din `strat3d`. */
export function colturi(p: Piesa): [number, number][] {
  const r = (p.deg * Math.PI) / 180, cos = Math.cos(r), sin = Math.sin(r);
  const w = p.w / 2, h = p.h / 2;
  return ([[-w, -h], [w, -h], [w, h], [-w, h]] as [number, number][])
    .map(([x, y]) => [p.cx + x * cos - y * sin, p.cy + x * sin + y * cos]);
}

/** Centrul de greutate al poligonului (al ariei, nu al vârfurilor: o cameră
 *  cu multe vârfuri pe o latură n-ar trage eticheta într-acolo). */
export function centru(poly: readonly number[][]): [number, number] {
  let a = 0, cx = 0, cy = 0;
  for (let i = 0; i < poly.length; i++) {
    const [x0, y0] = poly[i], [x1, y1] = poly[(i + 1) % poly.length];
    const f = x0 * y1 - x1 * y0;
    a += f; cx += (x0 + x1) * f; cy += (y0 + y1) * f;
  }
  if (Math.abs(a) < 1e-9) {
    return [poly.reduce((s, p) => s + p[0], 0) / poly.length, poly.reduce((s, p) => s + p[1], 0) / poly.length];
  }
  return [cx / (3 * a), cy / (3 * a)];
}

function eticheta(text: string, inaltime: number, la: THREE.Vector3, deArs: { dispose(): void }[]): THREE.Sprite {
  const tex = textura(text);
  const mat = tex
    ? new THREE.SpriteMaterial({ map: tex, depthWrite: false, transparent: true })
    : new THREE.SpriteMaterial({ color: 0xffffff, depthWrite: false });
  deArs.push(mat);
  if (tex) deArs.push(tex);
  const s = new THREE.Sprite(mat);
  const raport = tex ? tex.image.width / tex.image.height : 4;
  s.scale.set(inaltime * raport, inaltime, 1);
  s.position.copy(la);
  s.renderOrder = 4;
  s.userData.text = text;
  return s;
}

/** Textul pe un canvas: o pastilă albă, rotunjită, cu textul închis la
 *  culoare — ca eticheta din plan. `null` fără canvas 2D. */
function textura(text: string): THREE.CanvasTexture | null {
  if (typeof document === "undefined") return null;
  const cv = document.createElement("canvas");
  const ctx = cv.getContext("2d");
  if (!ctx) return null;
  const H = 64, pad = 18;
  ctx.font = "600 34px system-ui, -apple-system, 'Segoe UI', sans-serif";
  const W = Math.ceil(ctx.measureText(text).width) + 2 * pad;
  cv.width = W; cv.height = H;
  ctx.font = "600 34px system-ui, -apple-system, 'Segoe UI', sans-serif";
  ctx.fillStyle = "rgba(255,255,255,0.88)";
  ctx.beginPath();
  ctx.roundRect(1, 1, W - 2, H - 2, 14);
  ctx.fill();
  ctx.fillStyle = "#2b2622";
  ctx.textBaseline = "middle";
  ctx.fillText(text, pad, H / 2 + 1);
  const tex = new THREE.CanvasTexture(cv);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}
